"use client"

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
  SetStateAction,
  Dispatch,
  Component,
  useRef,
} from "react"
import dynamic from "next/dynamic"
import { HttpTypes } from "@medusajs/types"

const ConnectyCubeChatWidget = dynamic(
  () => import("@connectycube/chat-widget/react19"),
  { ssr: false }
)

interface ChatContextType {
  unreadMessageCount: number
  setUnreadMessageCount: Dispatch<SetStateAction<number>>
  /**
   * ConnectyCube 同一用户只能有一个活跃会话。
   * 打开显式聊天（消息页 / 卖家弹窗）时 acquire，关闭时 release，
   * 避免与后台未读监听实例并发登录导致 Login error。
   */
  acquireChatSession: (owner: string) => void
  releaseChatSession: (owner: string) => void
  isBackgroundChatEnabled: boolean
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

class ChatProviderErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    console.warn("[ChatProvider] ConnectyCube error swallowed:", error.message)
  }

  render() {
    if (this.state.hasError) return null
    return this.props.children
  }
}

interface ChatProviderProps {
  children: ReactNode
  user: HttpTypes.StoreCustomer | null
  CHAT_APP_ID: string
  CHAT_AUTH_KEY: string
}

/**
 * Delay background ConnectyCube until idle so first paint isn't blocked.
 * Do NOT start on pointerdown — logo / nav clicks race soft navigation.
 */
function useDeferredChatMount(enabled: boolean) {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!enabled) {
      return
    }

    let cancelled = false
    let idleId: number | undefined
    let timeoutId: number | undefined

    const enable = () => {
      if (!cancelled) setReady(true)
    }

    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(enable, { timeout: 2500 })
    } else {
      timeoutId = window.setTimeout(enable, 1500)
    }

    return () => {
      cancelled = true
      if (
        idleId !== undefined &&
        typeof window.cancelIdleCallback === "function"
      ) {
        window.cancelIdleCallback(idleId)
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) setReady(false)
  }, [enabled])

  return ready
}

function isChatConnectRaceError(reason: unknown): boolean {
  const msg =
    reason instanceof Error
      ? reason.message
      : typeof reason === "string"
        ? reason
        : String(reason ?? "")
  return (
    msg.includes("not connected to chat") ||
    msg.includes("call 'connect' function first")
  )
}

export const ChatProvider: React.FC<ChatProviderProps> = ({
  children,
  user,
  CHAT_APP_ID,
  CHAT_AUTH_KEY,
}) => {
  const [unreadMessageCount, setUnreadMessageCount] = useState(0)
  // Non-null => foreground chat owns the ConnectyCube session; unload background widget
  const [activeChatOwner, setActiveChatOwner] = useState<string | null>(null)
  const [originalTitle, setOriginalTitle] = useState(
    typeof document !== "undefined" ? document.title : ""
  )
  const userIdRef = useRef<string | null>(null)

  const chatAppKeys = useMemo(
    () => ({
      CHAT_APP_ID,
      CHAT_AUTH_KEY,
    }),
    [CHAT_APP_ID, CHAT_AUTH_KEY]
  )

  const acquireChatSession = useCallback((owner: string) => {
    setActiveChatOwner(owner)
  }, [])

  const releaseChatSession = useCallback((owner: string) => {
    setActiveChatOwner((current) => (current === owner ? null : current))
  }, [])

  const isBackgroundChatEnabled = !activeChatOwner
  const canUseChat = !!user && !!chatAppKeys.CHAT_APP_ID
  const deferredReady = useDeferredChatMount(canUseChat)

  const [idleLatch, setIdleLatch] = useState(false)
  useEffect(() => {
    const id = user?.id ?? null
    if (id !== userIdRef.current) {
      userIdRef.current = id
      setIdleLatch(false)
    }
  }, [user?.id])
  useEffect(() => {
    if (deferredReady && canUseChat) setIdleLatch(true)
    if (!canUseChat) setIdleLatch(false)
  }, [deferredReady, canUseChat])

  const showBackgroundListener =
    canUseChat && isBackgroundChatEnabled && idleLatch

  useEffect(() => {
    setOriginalTitle(document.title)
  }, [])

  useEffect(() => {
    const onRejection = (event: PromiseRejectionEvent) => {
      if (isChatConnectRaceError(event.reason)) {
        event.preventDefault()
        event.stopImmediatePropagation()
        console.warn("[ChatProvider] suppressed connect race:", event.reason)
      }
    }
    const onError = (event: ErrorEvent) => {
      if (isChatConnectRaceError(event.error ?? event.message)) {
        event.preventDefault()
        event.stopImmediatePropagation()
        console.warn("[ChatProvider] suppressed connect error:", event.message)
      }
    }
    // Capture phase so we beat Next.js overlay registration
    window.addEventListener("unhandledrejection", onRejection, true)
    window.addEventListener("error", onError, true)
    return () => {
      window.removeEventListener("unhandledrejection", onRejection, true)
      window.removeEventListener("error", onError, true)
    }
  }, [])

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (unreadMessageCount > 0) {
      let visible = true
      interval = setInterval(() => {
        document.title = visible
          ? `(${unreadMessageCount}) ${originalTitle}`
          : originalTitle
        visible = !visible
      }, 1000)
    } else {
      document.title = originalTitle
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [unreadMessageCount, originalTitle])

  const value = useMemo(
    () => ({
      unreadMessageCount,
      setUnreadMessageCount,
      acquireChatSession,
      releaseChatSession,
      isBackgroundChatEnabled,
    }),
    [
      unreadMessageCount,
      acquireChatSession,
      releaseChatSession,
      isBackgroundChatEnabled,
    ]
  )

  return (
    <ChatContext.Provider value={value}>
      {children}
      {showBackgroundListener && (
        <div className="hidden">
          <ChatProviderErrorBoundary>
            <ConnectyCubeChatWidget
              appId={chatAppKeys.CHAT_APP_ID + ""}
              authKey={chatAppKeys.CHAT_AUTH_KEY}
              config={{ debug: { mode: 0 } }}
              userId={user!.id}
              userName={`${user!.first_name || "Anonymous"} ${user!.last_name || ""}`}
              splitView={false}
              open={false}
              hideWidgetButton={true}
              hideNewChatButton
              onUnreadCountChange={(count: number) =>
                setUnreadMessageCount(count)
              }
              showNotifications
              webPushNotifications={false}
            />
          </ChatProviderErrorBoundary>
        </div>
      )}
    </ChatContext.Provider>
  )
}

export const useChat = () => {
  const context = useContext(ChatContext)
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider")
  }
  return context
}
